import UserContext from "Context/UserProvider";
import { useContext, useEffect, useState } from "react";
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from "react-router-dom";
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select as MuiSelect,
    Typography,
} from '@mui/material';
import {
    Timeline,
    ElectricBolt,
    Save as SaveIcon
} from '@mui/icons-material';
import { IoMdClose } from "react-icons/io";
import ModalComponent from "components/common/Modal/Modal";
import DashboardCard from "components/dashboard/common/DashboardCard";
import { useApiService } from "../../services/ApiService/ApiService";

interface RateCardOption {
    uuid?: string;
    name: string;
    value: string;
    label: string;
}

interface FrequencyOption {
    name: string;
    value: string;
    label: string;
}

function ChargingPartnerRequestPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const location = useLocation();
    const [isDisabled, setIsDisabled] = useState(false);
    const navigate = useNavigate();

    const [rateCardOptions, setRateCardOptions] = useState<RateCardOption[]>([{ name: "None", value: "", label: "None" }]);
    const [rateCardValue, setRateCardValue] = useState<RateCardOption>({ name: "None", value: "", label: "None" });
    const [frequencyOptions, setFrequencyOptions] = useState<FrequencyOption[]>([{ name: "ONCE", value: "ONCE", label: "ONCE" }]);
    const [frequencyValue, setFrequencyValue] = useState<FrequencyOption>({ name: "ONCE", value: "ONCE", label: "ONCE" });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [isError, setIsError] = useState(false);

    // Accessing the state passed through navigation
    const state = location.state as {
        chargingPartnerUuid: string;
        chargingPartnerBusinessName: string;
        workflowState: string;
        businessCategory: string;
        canOfferCharging: boolean;
        canRequestCharging: boolean;
        partnershipRequestType: string;
        consumerBusinessUuid: string;
        providerBusinessUuid: string;
    };

    useEffect(() => {
        if (state?.partnershipRequestType !== "REQUEST") {
            fetchChargingPartner();
        }
        setLoading(false);
    }, []);

    const closeModal = () => {
        setIsOpen(false);
    };

    const handleRateCardChange = (event: any) => {
        const selectedRateCard = rateCardOptions.find((i) => i.uuid === event.target.value);
        if (selectedRateCard) {
            setRateCardValue(selectedRateCard);
        }
    };

    const handleFrequencyChange = (event: any) => {
        const selectedFrequency = frequencyOptions.find((i) => i.value === event.target.value);
        if (selectedFrequency) {
            setFrequencyValue(selectedFrequency);
        }
    };

    async function fetchChargingPartner() {
        let bodyData: { [name: string]: any } = {};
        bodyData["uuid"] = userInfo.uuid;
        bodyData["businessUuid"] = userInfo.businessUuid;
        bodyData["accountUuid"] = userInfo.accountUuid;

        try {
            const response = await fetchWithAuth('/business/get', {
                method: 'POST',
                body: JSON.stringify(bodyData),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch charging partner');
            }

            const res_json = await response.json();
            setRateCardOptions([
                { name: "None", value: "", label: "None" },
                ...res_json.business.rateCards.map((e: any) => ({
                    uuid: e.uuid,
                    name: e.name,
                    value: e.uuid,
                    label: e.name
                }))
            ]);

            const options = res_json.business.frequencies.map((frequency: string) => ({
                name: frequency,
                value: frequency,
                label: frequency
            }));
            setFrequencyOptions(options);

            if (options.length > 0) {
                setFrequencyValue(options[0]);
            }
        } catch (error) {
            console.error('Error fetching charging partner:', error);
            toast.error('Failed to fetch charging partner data');
        } finally {
            setLoading(false);
        }
    }

    async function submitChargingPartnership() {
        setIsDisabled(true);
        setSaving(true);
        let bodyData: { [name: string]: any } = {};
        bodyData["uuid"] = userInfo.uuid;

        if (state.partnershipRequestType === "REQUEST") {
            bodyData["providerBusinessUuid"] = state.consumerBusinessUuid;
            bodyData["consumerBusinessUuid"] = state.providerBusinessUuid;
        } else if (state.partnershipRequestType === "OFFER") {
            bodyData["providerBusinessUuid"] = state.providerBusinessUuid;
            bodyData["consumerBusinessUuid"] = state.consumerBusinessUuid;
        }

        bodyData["selectedRateCardUuid"] = rateCardValue.uuid;
        bodyData["partnershipRequestType"] = state.partnershipRequestType;
        bodyData["selectedFrequency"] = frequencyValue.value;

        try {
            const response = await fetchWithAuth('/charging-partner/request', {
                method: 'POST',
                body: JSON.stringify(bodyData),
            });

            const res_json = await response.json();

            if (response.ok) {
                toast.success('Charging Partner Submit Successful');
                navigate("/share/business/charging-partners");
            } else if (response.status === 400) {
                switch (res_json.code) {
                    case "CHARGING_PARTNER_RATE_CARD_UUID_IS_MANDATORY":
                        setErrorMessage("Please select a Rate Card from the list");
                        break;
                    default:
                        setErrorMessage(`An error (${res_json.code}) occurred whilst saving this business.`);
                        break;
                }
                setIsError(true);
                setIsOpen(true);
            }
        } catch (error) {
            console.error('Error submitting charging partnership:', error);
            toast.error('Failed to submit charging partnership');
        } finally {
            setIsDisabled(false);
            setSaving(false);
        }
    }

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage, {
                position: 'bottom-center',
                duration: 5000,
            });
        }
        setErrorMessage(null);
    }, [errorMessage]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <Backdrop open={saving} sx={{ zIndex: 1300 }}>
                <CircularProgress />
            </Backdrop>

            <ModalComponent modalIsOpen={[modalIsOpen, setIsOpen]}>
                <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="flex-end">
                        <IoMdClose size={30} style={{ cursor: 'pointer' }} onClick={closeModal} />
                    </Box>
                    <Box sx={{ textAlign: 'center', my: 3 }}>
                        {isError && errorMessage && (
                            <Typography variant="h6" color="error">
                                {errorMessage}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </ModalComponent>

            <Container maxWidth="xl">
                <Box sx={{ pr: 3 }}>
                    <Grid container spacing={3}>
                        {/* Request Type Card */}
                        <Grid item xs={12} md={6}>
                            <DashboardCard
                                title="Request Type"
                                icon={<Timeline />}
                                accentColor="primary"
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <FormControl component="fieldset">
                                        <RadioGroup>
                                            {(state.canRequestCharging && state.partnershipRequestType === 'REQUEST') && (
                                                <FormControlLabel
                                                    value="REQUEST"
                                                    control={
                                                        <Radio
                                                            checked={state.partnershipRequestType === 'REQUEST'}
                                                            disabled={state.workflowState === 'REQUESTED'}
                                                        />
                                                    }
                                                    label="Request Charging Services"
                                                    sx={{
                                                        color: state.workflowState === 'REQUESTED' ? 'text.disabled' : 'text.primary',
                                                    }}
                                                />
                                            )}
                                            {(state.canOfferCharging && state.partnershipRequestType === 'OFFER') && (
                                                <FormControlLabel
                                                    value="OFFER"
                                                    control={
                                                        <Radio
                                                            checked={state.partnershipRequestType === 'OFFER'}
                                                        />
                                                    }
                                                    label="Offer Charging Services"
                                                />
                                            )}
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                            </DashboardCard>
                        </Grid>

                        {/* Rate Card Selection */}
                        {(state.partnershipRequestType === 'OFFER') && (
                            <Grid item xs={12} md={6}>
                                <DashboardCard
                                    title="Rate Card"
                                    icon={<ElectricBolt />}
                                    accentColor="secondary"
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <FormControl fullWidth>
                                            <InputLabel>Select Rate Card</InputLabel>
                                            <MuiSelect
                                                value={rateCardValue.uuid || rateCardValue.value}
                                                onChange={handleRateCardChange}
                                                label="Select Rate Card"
                                            >
                                                {rateCardOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.uuid || option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </MuiSelect>
                                        </FormControl>
                                    </Box>
                                </DashboardCard>
                            </Grid>
                        )}

                        {/* Submit Button */}
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" mt={2}>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={submitChargingPartnership}
                                    disabled={isDisabled}
                                    size="large"
                                >
                                    Submit Request
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
}

export default ChargingPartnerRequestPage;
